/**
 * ReportBlockBuilder — manual Track 2 recipe editor.
 * Add / remove / reorder blocks; select one to edit config.
 *
 * Block shape: { instance_id, block_type, block_id, order, enabled, config }
 */

import { useCallback, useMemo, useRef, useState } from "react";
import {
  defaultConfigForBlockType,
  isRepeatableBlockType,
  normalizeDraftBlock,
  normalizeDraftBlocks,
} from "./reportProfileDefaults";

function DragHandle() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-4 h-4 text-gray-300 flex-shrink-0 cursor-grab active:cursor-grabbing"
      aria-hidden
    >
      <path d="M7 2a2 2 0 110 4 2 2 0 010-4zm6 0a2 2 0 110 4 2 2 0 010-4zM7 8a2 2 0 110 4 2 2 0 010-4zm6 0a2 2 0 110 4 2 2 0 010-4zM7 14a2 2 0 110 4 2 2 0 010-4zm6 0a2 2 0 110 4 2 2 0 010-4z" />
    </svg>
  );
}

function BlockConfigPanel({
  entry,
  catalogMeta,
  sectionLabels = [],
  onChangeConfig,
  onClose,
}) {
  if (!entry) return null;
  const type = entry.block_type || entry.block_id;
  const cfg = entry.config || {};
  const name = catalogMeta?.name || type;

  const setField = (key, value) => {
    onChangeConfig({ ...cfg, [key]: value });
  };

  return (
    <div className="rounded-xl border border-[#004f73]/25 bg-slate-50 p-3 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#004f73]">
            Block settings
          </p>
          <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
          <p className="text-[11px] text-slate-500 font-mono truncate">{type}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1 rounded-lg hover:bg-white"
        >
          Close
        </button>
      </div>

      {type === "page_break" ? (
        <p className="text-xs text-slate-500">
          Forces a new PDF page before the next block. No extra settings.
        </p>
      ) : null}

      {type === "static_text" ? (
        <>
          <label className="block text-xs">
            <span className="font-semibold text-slate-600 mb-1 block">Title</span>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={cfg.title || ""}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Section title"
            />
          </label>
          <label className="block text-xs">
            <span className="font-semibold text-slate-600 mb-1 block">HTML body</span>
            <textarea
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[120px] font-mono"
              value={cfg.html || ""}
              onChange={(e) => setField("html", e.target.value)}
              placeholder="<p>Your content…</p>"
            />
          </label>
        </>
      ) : null}

      {type === "static_heading" ? (
        <>
          <label className="block text-xs">
            <span className="font-semibold text-slate-600 mb-1 block">Heading</span>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={cfg.title || ""}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Section heading"
            />
          </label>
          <label className="block text-xs">
            <span className="font-semibold text-slate-600 mb-1 block">Subtitle</span>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={cfg.subtitle || ""}
              onChange={(e) => setField("subtitle", e.target.value)}
              placeholder="Optional subtitle"
            />
          </label>
        </>
      ) : null}

      {type === "section_scores" ? (
        <>
          <label className="block text-xs">
            <span className="font-semibold text-slate-600 mb-1 block">Title</span>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={cfg.title || "Section Scores"}
              onChange={(e) => setField("title", e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={cfg.show_charts !== false}
              onChange={(e) => setField("show_charts", e.target.checked)}
              className="rounded border-slate-300"
            />
            Show charts
          </label>
          {sectionLabels.length ? (
            <fieldset className="space-y-1.5">
              <legend className="text-xs font-semibold text-slate-600 mb-1">
                Include sections
              </legend>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={cfg.include_sections === "*" || !cfg.include_sections}
                  onChange={(e) => {
                    if (e.target.checked) setField("include_sections", "*");
                    else setField("include_sections", [...sectionLabels]);
                  }}
                  className="rounded border-slate-300"
                />
                All sections
              </label>
              {cfg.include_sections !== "*" && Array.isArray(cfg.include_sections)
                ? sectionLabels.map((label) => {
                    const selected = cfg.include_sections.includes(label);
                    return (
                      <label
                        key={label}
                        className="flex items-center gap-2 text-xs text-slate-700 pl-4"
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...cfg.include_sections, label]
                              : cfg.include_sections.filter((s) => s !== label);
                            setField(
                              "include_sections",
                              next.length ? next : [...sectionLabels]
                            );
                          }}
                          className="rounded border-slate-300"
                        />
                        {label}
                      </label>
                    );
                  })
                : null}
            </fieldset>
          ) : (
            <p className="text-[11px] text-slate-500">
              Section list follows the assessment format (all sections by default).
            </p>
          )}
        </>
      ) : null}

      {!["page_break", "static_text", "static_heading", "section_scores"].includes(type) ? (
        <label className="block text-xs">
          <span className="font-semibold text-slate-600 mb-1 block">
            Display title <span className="font-normal text-slate-400">(optional)</span>
          </span>
          <input
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            value={cfg.title || ""}
            onChange={(e) => setField("title", e.target.value)}
            placeholder={name}
          />
        </label>
      ) : null}
    </div>
  );
}

function BlockCard({
  block,
  entry,
  isEnabled,
  isSelected,
  isDragging,
  isDragOver,
  onSelect,
  onToggle,
  onRemove,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDragOver,
  onDrop,
  touchReorder = false,
  canMoveUp = false,
  canMoveDown = false,
  onMoveUp,
  onMoveDown,
  allowAddAnother = false,
  onAddAnother,
}) {
  const cfgHint = (entry?.config?.title || "").trim();

  return (
    <div
      draggable={isEnabled}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={[
        "report-block-card flex items-start gap-3 rounded-lg border p-3 transition-all select-none",
        isSelected
          ? "border-[#004f73] ring-2 ring-[#004f73]/20 bg-white"
          : isEnabled
            ? "border-[#004f73]/30 bg-white"
            : "border-gray-200 bg-gray-50 opacity-55",
        isDragging ? "opacity-30 scale-95" : "",
        isDragOver && isEnabled ? "border-[#004f73] ring-2 ring-[#004f73]/20 bg-blue-50/30" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={`mt-0.5 flex-shrink-0 ${isEnabled ? "opacity-100" : "opacity-0"}`}>
        <DragHandle />
      </div>

      <button
        type="button"
        onClick={() => onToggle()}
        title={isEnabled ? "Disable in report" : "Add to report"}
        className={[
          "mt-0.5 w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-colors",
          isEnabled
            ? "border-[#004f73] bg-[#004f73] text-white"
            : "border-gray-300 bg-white text-gray-300",
        ].join(" ")}
      >
        {isEnabled ? (
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        ) : null}
      </button>

      <button
        type="button"
        onClick={onSelect}
        className="flex-1 min-w-0 text-left"
        title="Edit block settings"
      >
        <p className="text-sm font-semibold text-gray-800 leading-tight">{block.name}</p>
        {cfgHint ? (
          <p className="text-[11px] text-[#004f73] mt-0.5 truncate">{cfgHint}</p>
        ) : (
          <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">
            {block.description}
          </p>
        )}
        {isEnabled ? (
          <p className="text-[10px] text-slate-400 mt-1">
            {isSelected ? "Editing settings ↓" : "Click to configure"}
          </p>
        ) : null}
      </button>

      <div className="flex flex-col gap-0.5 shrink-0">
        {touchReorder && isEnabled ? (
          <>
            <button
              type="button"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              aria-label={`Move ${block.name} up`}
              className="h-8 w-8 rounded-lg border border-gray-200 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              aria-label={`Move ${block.name} down`}
              className="h-8 w-8 rounded-lg border border-gray-200 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              ↓
            </button>
          </>
        ) : null}
        {allowAddAnother && isEnabled ? (
          <button
            type="button"
            onClick={onAddAnother}
            title="Add another"
            className="h-8 px-2 rounded-lg border border-gray-200 bg-white text-[10px] font-medium text-[#004f73] hover:bg-slate-50"
          >
            +1
          </button>
        ) : null}
        {isEnabled ? (
          <button
            type="button"
            onClick={onRemove}
            title="Remove from recipe"
            className="h-8 w-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-red-600 hover:bg-red-50"
            aria-label={`Remove ${block.name}`}
          >
            ×
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function ReportBlockBuilder({
  catalog = [],
  selectedBlocks = [],
  onChange,
  touchReorder = false,
  sectionLabels = [],
  showConfigPanel = true,
}) {
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  const dragItem = useRef(null);

  const normalized = useMemo(
    () => normalizeDraftBlocks(selectedBlocks),
    [selectedBlocks]
  );

  const emit = useCallback(
    (next) => {
      onChange(normalizeDraftBlocks(next));
    },
    [onChange]
  );

  const enabled = useMemo(
    () =>
      normalized
        .filter((b) => b.enabled !== false)
        .sort((a, b) => a.order - b.order),
    [normalized]
  );

  const enabledTypeCounts = useMemo(() => {
    const counts = {};
    enabled.forEach((b) => {
      const t = b.block_type || b.block_id;
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [enabled]);

  const availableCatalog = useMemo(() => {
    return (catalog || []).filter((meta) => {
      const id = meta.block_id;
      if (isRepeatableBlockType(id)) return true;
      return !enabledTypeCounts[id];
    });
  }, [catalog, enabledTypeCounts]);

  const blockById = useMemo(
    () => Object.fromEntries((catalog || []).map((b) => [b.block_id, b])),
    [catalog]
  );

  const selectedEntry = enabled.find((b) => b.instance_id === selectedInstanceId) || null;

  const addBlock = useCallback(
    (blockType) => {
      const maxOrder = Math.max(0, ...normalized.map((b) => b.order || 0));
      const entry = normalizeDraftBlock(
        {
          block_id: blockType,
          block_type: blockType,
          order: maxOrder + 1,
          enabled: true,
          config: defaultConfigForBlockType(blockType),
        },
        normalized.length
      );
      const next = [...normalized, entry];
      emit(next);
      setSelectedInstanceId(entry.instance_id);
    },
    [normalized, emit]
  );

  const handleToggleCatalog = useCallback(
    (blockType) => {
      if (isRepeatableBlockType(blockType)) {
        addBlock(blockType);
        return;
      }
      const existing = normalized.find(
        (b) => (b.block_type || b.block_id) === blockType && b.enabled !== false
      );
      if (existing) {
        emit(
          normalized.map((b) =>
            b.instance_id === existing.instance_id ? { ...b, enabled: false } : b
          )
        );
        if (selectedInstanceId === existing.instance_id) setSelectedInstanceId(null);
        return;
      }
      const disabled = normalized.find(
        (b) => (b.block_type || b.block_id) === blockType && b.enabled === false
      );
      if (disabled) {
        const maxOrder = Math.max(
          0,
          ...normalized.filter((b) => b.enabled !== false).map((b) => b.order || 0)
        );
        emit(
          normalized.map((b) =>
            b.instance_id === disabled.instance_id
              ? { ...b, enabled: true, order: maxOrder + 1 }
              : b
          )
        );
        setSelectedInstanceId(disabled.instance_id);
        return;
      }
      addBlock(blockType);
    },
    [normalized, emit, addBlock, selectedInstanceId]
  );

  const removeInstance = useCallback(
    (instanceId) => {
      emit(normalized.filter((b) => b.instance_id !== instanceId));
      if (selectedInstanceId === instanceId) setSelectedInstanceId(null);
    },
    [normalized, emit, selectedInstanceId]
  );

  const disableInstance = useCallback(
    (instanceId) => {
      emit(
        normalized.map((b) =>
          b.instance_id === instanceId ? { ...b, enabled: false } : b
        )
      );
      if (selectedInstanceId === instanceId) setSelectedInstanceId(null);
    },
    [normalized, emit, selectedInstanceId]
  );

  const updateConfig = useCallback(
    (instanceId, config) => {
      emit(
        normalized.map((b) =>
          b.instance_id === instanceId ? { ...b, config: { ...config } } : b
        )
      );
    },
    [normalized, emit]
  );

  const moveBlock = useCallback(
    (instanceId, direction) => {
      const list = [...enabled];
      const idx = list.findIndex((b) => b.instance_id === instanceId);
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (idx < 0 || swapIdx < 0 || swapIdx >= list.length) return;
      [list[idx], list[swapIdx]] = [list[swapIdx], list[idx]];
      const orderMap = Object.fromEntries(list.map((b, i) => [b.instance_id, i + 1]));
      emit(
        normalized.map((b) =>
          orderMap[b.instance_id] != null ? { ...b, order: orderMap[b.instance_id] } : b
        )
      );
    },
    [enabled, normalized, emit]
  );

  const handleDragStart = useCallback((e, instanceId) => {
    dragItem.current = instanceId;
    setDraggingId(instanceId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", instanceId);
  }, []);

  const handleDragEnter = useCallback((e, instanceId) => {
    e.preventDefault();
    if (instanceId !== dragItem.current) setDragOverId(instanceId);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback(
    (e, targetId) => {
      e.preventDefault();
      const sourceId = dragItem.current;
      if (!sourceId || sourceId === targetId) return;
      const sourceIdx = enabled.findIndex((b) => b.instance_id === sourceId);
      const targetIdx = enabled.findIndex((b) => b.instance_id === targetId);
      if (sourceIdx < 0 || targetIdx < 0) return;
      const list = [...enabled];
      const [removed] = list.splice(sourceIdx, 1);
      list.splice(targetIdx, 0, removed);
      const orderMap = Object.fromEntries(list.map((b, i) => [b.instance_id, i + 1]));
      emit(
        normalized.map((b) =>
          orderMap[b.instance_id] != null ? { ...b, order: orderMap[b.instance_id] } : b
        )
      );
      setDraggingId(null);
      setDragOverId(null);
      dragItem.current = null;
    },
    [enabled, normalized, emit]
  );

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDragOverId(null);
    dragItem.current = null;
  }, []);

  if (!catalog.length) {
    return (
      <div className="text-center py-8 text-gray-400 text-sm">Loading available blocks…</div>
    );
  }

  return (
    <div className="report-block-builder space-y-3">
      {enabled.length > 0 ? (
        <p className="text-[11px] font-semibold text-[#004f73] uppercase tracking-wide">
          In report — {touchReorder ? "use arrows or drag to reorder" : "drag to reorder"}
        </p>
      ) : null}

      <div className="space-y-1.5">
        {enabled.map((entry, idx) => {
          const type = entry.block_type || entry.block_id;
          const block = blockById[type] || {
            block_id: type,
            name: type.replace(/_/g, " "),
            description: "",
          };
          return (
            <BlockCard
              key={entry.instance_id}
              block={block}
              entry={entry}
              isEnabled
              isSelected={selectedInstanceId === entry.instance_id}
              isDragging={draggingId === entry.instance_id}
              isDragOver={dragOverId === entry.instance_id}
              onSelect={() =>
                setSelectedInstanceId((cur) =>
                  cur === entry.instance_id ? null : entry.instance_id
                )
              }
              onToggle={() => disableInstance(entry.instance_id)}
              onRemove={() => removeInstance(entry.instance_id)}
              onDragStart={(e) => handleDragStart(e, entry.instance_id)}
              onDragEnter={(e) => handleDragEnter(e, entry.instance_id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, entry.instance_id)}
              onDragEnd={handleDragEnd}
              touchReorder={touchReorder}
              canMoveUp={idx > 0}
              canMoveDown={idx < enabled.length - 1}
              onMoveUp={() => moveBlock(entry.instance_id, "up")}
              onMoveDown={() => moveBlock(entry.instance_id, "down")}
              allowAddAnother={isRepeatableBlockType(type)}
              onAddAnother={() => addBlock(type)}
            />
          );
        })}
      </div>

      {showConfigPanel && selectedEntry ? (
        <BlockConfigPanel
          entry={selectedEntry}
          catalogMeta={blockById[selectedEntry.block_type || selectedEntry.block_id]}
          sectionLabels={sectionLabels}
          onChangeConfig={(config) => updateConfig(selectedEntry.instance_id, config)}
          onClose={() => setSelectedInstanceId(null)}
        />
      ) : null}

      {enabled.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-3">
          Add blocks below to build this report recipe.
        </p>
      ) : null}

      {availableCatalog.length ? (
        <>
          <div className="flex items-center gap-2 my-2">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
              Available to add
            </span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <div className="space-y-1.5">
            {availableCatalog.map((block) => (
              <BlockCard
                key={`avail-${block.block_id}`}
                block={block}
                entry={null}
                isEnabled={false}
                isSelected={false}
                isDragging={false}
                isDragOver={false}
                onSelect={() => handleToggleCatalog(block.block_id)}
                onToggle={() => handleToggleCatalog(block.block_id)}
                onRemove={() => {}}
                onDragStart={() => {}}
                onDragEnter={() => {}}
                onDragOver={() => {}}
                onDrop={() => {}}
                onDragEnd={() => {}}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
